import React, { useMemo } from 'react';
import { violationsColumnDefs } from './violations-column-defs';

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { cn } from '../../../utils/cn';
import { ITEMS_PER_PAGE } from '../constants';
import { OnViolationStatusChange, ViolationNode } from '../types';
import { ViolationsLoadingSkeleton } from './violations-loading-skeleton';
import { ViolationsNoRecords } from './violations-no-records';

export interface ViolationsGridProps {
  data: ViolationNode[];
  page: number;
  setPage: (page: number) => void;
  totalRows: number;
  onRowClick?: (row: ViolationNode) => void;
  onStatusChange: OnViolationStatusChange;
  isLoading: boolean;
}

export const ViolationsGrid: React.FC<ViolationsGridProps> = ({
  data,
  page,
  setPage,
  totalRows,
  onRowClick,
  onStatusChange,
  isLoading,
}) => {
  const columns = useMemo(
    () => violationsColumnDefs(onStatusChange),
    [onStatusChange]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalRows / ITEMS_PER_PAGE),
    state: { pagination: { pageIndex: page, pageSize: ITEMS_PER_PAGE } },
    manualPagination: true,
  });

  const getTableContent = () => {
    if (isLoading) {
      return <ViolationsLoadingSkeleton />;
    }

    if (!data.length) {
      return <ViolationsNoRecords noOfColumns={columns.length} />;
    }

    return (
      <TableBody>
        {table.getRowModel().rows.map((row, index) => (
          <TableRow
            key={row.id}
            onClick={() => onRowClick?.(row.original)}
            className={cn(
              'cursor-pointer',
              // Odd rows are light gray
              index % 2 === 1 && 'bg-gray-100'
            )}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className={cell.column.columnDef.meta?.className}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    );
  };

  return (
    <div className="flex gap-4 flex-col h-full">
      <Table className="w-full" data-testid="violations-table">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        {getTableContent()}
      </Table>
    </div>
  );
};

export default ViolationsGrid;
